import React from 'react';
import { GlobalProvider } from 'GlobalContext';
import { BrowserRouter } from 'react-router-dom';
import { AuthenticationContextProvider } from 'services/authentication/AuthenticationContext';
import { FetchContextProvider } from 'services/api/FetchContext';
import { SettingsContextProvider } from 'services/settings/SettingsContext';
import { SettingsDivisionProvider } from 'components/settings/SettingsDivisionContext';
import { ModalProvider } from 'components/modals/ModalContext';
import { TimerProvider } from 'services/timer/TimerContext';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Navbar from 'components/layout/Navbar';
import Routes from './routes';
import GenericModal from 'components/modals/GenericModal';
import FloatingAlert from 'components/alerts/FloatingAlert';
import FooterLogo from 'components/layout/FooterLogo';
import AppLayout from 'AppLayout';
import axios from 'axios';
import { getTabEnvironmentHash } from 'services/authentication/environmentScope';

// Point the frontend base url to the Heroku url or to the localhost
export const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost';

// Configure api base url
export const apiBaseUrl = `${baseUrl}/api/frontend`;

// Send the environment this tab is pinned to with every frontend api request,
// the backend scopes a superuser or staff user to it instead of the stored environment
axios.interceptors.request.use((config) => {
    const tabEnvironmentHash = getTabEnvironmentHash();
    if (tabEnvironmentHash && config.url && config.url.startsWith(apiBaseUrl)) {
        config.headers = config.headers ?? {};
        config.headers['X-Environment-Hash'] = tabEnvironmentHash;
    }
    return config;
});

// Configure websocket base url following the base url host and port
export const websocketBaseUrl = process.env.NODE_ENV === 'production'
    ? `wss://${window.location.host}/ws`
    : `${baseUrl.replace('http', 'ws')}/ws`;

// Determine if the browser if online
export const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

// Configure stripe public key and stripe promise
const stripeKeys: Record<string, string | undefined> = {
    staging: process.env.REACT_APP_STRIPE_SANDBOX_PUBLIC_KEY ?? "",
    production: process.env.REACT_APP_STRIPE_LIVE_PUBLIC_KEY ?? "",
};

export const stripePublicKey = stripeKeys[process.env.REACT_APP_FRONTEND_ENV ?? ""] ?? "";
const stripePromise = isOnline && stripePublicKey ? loadStripe(stripePublicKey) : null;

function App() {
    return (
        // <div className='app-container'>
            <GlobalProvider>
                <BrowserRouter>
                    <AppLayout>
                        <AuthenticationContextProvider>
                            <FetchContextProvider>
                                <SettingsContextProvider>
                                    <SettingsDivisionProvider>
                                        <ModalProvider>
                                            <TimerProvider>
                                            <Elements stripe={stripePromise}>
                                                <Navbar />
                                                <main>
                                                    <Routes />
                                                    <GenericModal />
                                                    <FloatingAlert />
                                                </main>
                                                <FooterLogo />
                                            </Elements>
                                            </TimerProvider>
                                        </ModalProvider>
                                    </SettingsDivisionProvider>
                                </SettingsContextProvider>
                            </FetchContextProvider>
                        </AuthenticationContextProvider>
                    </AppLayout>
                </BrowserRouter>
            </GlobalProvider>
        // </div>
    );
}

export default App;